import { type OperationVariable, type VarValue, isCredentialValue } from '@/hooks/useDeviceActions';
import CredentialSelect from '@/components/CredentialSelect';
import { formatVariableName } from '@/utils/formatting';
import { addressVariableProblem, eui64Prefix, isAddressVariable, isWholeNumberVariable } from '@/utils/netValidation';
import { VlanRangeListInput } from '@/components/VlanRangeListInput';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** What a field that may be left empty says while it is: what it is then. */
function leftOutPlaceholder(v: OperationVariable): string | undefined {
  if (v.default == null) return undefined;
  return v.default === '' ? 'Optional' : `Default: ${v.default}`;
}

/** An example value in the variable's own family and form. */
function addressPlaceholder(varType: string, family: OperationVariable['family']): string {
  if (varType === 'eui64_prefix') return 'e.g. fd00:1:2:3::';
  if (varType === 'interface_address') return family === 'ipv6' ? 'e.g. 2001:db8::1/64' : 'e.g. 192.0.2.1/24';
  if (varType === 'prefix') return family === 'ipv4' ? 'e.g. 10.70.0.0/16' : 'e.g. 2001:db8:70::/64';
  return family === 'ipv6' ? 'e.g. fe80::1' : 'e.g. 192.168.1.1';
}

interface OperationVariableInputsProps {
  variables: OperationVariable[];
  /** Scalar + typed values, keyed by variable name. */
  values: Record<string, VarValue>;
  onValuesChange: (next: Record<string, VarValue>) => void;
  /** Multi-select values (e.g. several interfaces), keyed by variable name. */
  multiValues: Record<string, string[]>;
  onMultiChange: (next: Record<string, string[]>) => void;
  disabled?: boolean;
  idPrefix: string;
}

/**
 * Renders the input controls for a vendor operation's variables, driven by the
 * `var_type` / `possibleValues` / `multiSelect` / `allowCustom` metadata from
 * available-operations. Shared by the device Actions tab and the CM step editor
 * so both get the same device-aware lookups (interface/VLAN dropdowns) and the
 * VLAN-range editor.
 */
export function OperationVariableInputs({
  variables,
  values,
  onValuesChange,
  multiValues,
  onMultiChange,
  disabled,
  idPrefix,
}: OperationVariableInputsProps) {
  const setScalar = (name: string, v: VarValue) => {
    onValuesChange({ ...values, [name]: v });
  };

  return (
    <>
      {variables.map((v) => {
        const raw = values[v.name];
        const strVal = typeof raw === 'string' ? raw : '';
        const id = `${idPrefix}-${v.name}`;
        return (
          <div key={v.name} className="space-y-1.5 mb-4">
            <Label htmlFor={id} className="text-xs">
              {formatVariableName(v.name)}
              {v.default != null ? <span className="text-muted-foreground font-normal"> (optional)</span> : null}
            </Label>

            {v.varType === 'credential' ? (
              <CredentialSelect
                id={id}
                value={isCredentialValue(raw) ? raw.credentialId : null}
                onChange={(credentialId) => { setScalar(v.name, credentialId ? { credentialId } : ''); }}
                regionId={v.credentialScope?.regionId ?? ''}
                divisionId={v.credentialScope?.divisionId ?? null}
                types={v.credentialTypes ?? []}
                disabled={disabled}
              />
            ) : v.varType === 'vlan_range_list' ? (
              <VlanRangeListInput
                value={Array.isArray(raw) ? raw : []}
                onChange={(ranges) => { setScalar(v.name, ranges); }}
                disabled={disabled}
                idPrefix={id}
              />
            ) : v.possibleValues && v.multiSelect ? (
              <div className="space-y-1.5">
                {v.possibleValues.map((opt) => {
                  const selected = multiValues[v.name] ?? [];
                  const isChecked = selected.includes(opt.value);
                  return (
                    <label key={opt.value} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={isChecked}
                        disabled={disabled}
                        onCheckedChange={() => {
                          const current = multiValues[v.name] ?? [];
                          const next = isChecked ? current.filter((val) => val !== opt.value) : [...current, opt.value];
                          onMultiChange({ ...multiValues, [v.name]: next });
                        }}
                      />
                      {opt.label}
                    </label>
                  );
                })}
                <p className="text-xs text-muted-foreground">{(multiValues[v.name] ?? []).length} selected</p>
              </div>
            ) : v.possibleValues && v.allowCustom ? (
              <>
                <Input
                  id={id}
                  type={isWholeNumberVariable(v.varType) ? 'number' : 'text'}
                  min={v.min ?? undefined}
                  max={v.max ?? undefined}
                  value={strVal}
                  onChange={(e) => { setScalar(v.name, e.target.value); }}
                  disabled={disabled}
                  className="font-mono"
                />
                {v.possibleValues.length > 0 ? (
                  <div className="mt-1">
                    <p className="text-xs text-muted-foreground mb-1">Already in use:</p>
                    <div className="flex flex-wrap gap-1">
                      {v.possibleValues.map((opt) => (
                        <Badge
                          key={opt.value}
                          className={cn(
                            'border-0 text-[10px] font-normal cursor-pointer',
                            strVal === opt.value ? 'bg-sev-1/15 text-sev-1' : 'bg-muted text-muted-foreground',
                          )}
                          onClick={() => { if (!disabled) setScalar(v.name, opt.value); }}
                        >
                          {opt.label}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ) : null}
              </>
            ) : v.possibleValues ? (
              <Select value={strVal} onValueChange={(val) => { setScalar(v.name, val); }} disabled={disabled}>
                <SelectTrigger id={id}>
                  <SelectValue placeholder={`Select ${formatVariableName(v.name)}…`} />
                </SelectTrigger>
                <SelectContent>
                  {v.possibleValues.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : isAddressVariable(v.varType) ? (
              <>
                <Input
                  id={id}
                  type="text"
                  placeholder={addressPlaceholder(v.varType, v.family)}
                  value={strVal}
                  onChange={(e) => { setScalar(v.name, e.target.value); }}
                  disabled={disabled}
                  className={cn(
                    'font-mono',
                    strVal !== '' && addressVariableProblem(v.varType, strVal, v.family) ? 'border-destructive' : '',
                  )}
                />
                {strVal !== '' && addressVariableProblem(v.varType, strVal, v.family) ? (
                  <p className="text-xs text-destructive">
                    {addressVariableProblem(v.varType, strVal, v.family)}
                  </p>
                ) : v.varType === 'eui64_prefix' && strVal !== '' ? (
                  <p className="text-xs text-muted-foreground">
                    Sent as <span className="font-mono">{eui64Prefix(strVal).value}</span>
                  </p>
                ) : null}
              </>
            ) : isWholeNumberVariable(v.varType) ? (
              <Input
                id={id}
                type="number"
                min={v.min ?? undefined}
                max={v.max ?? undefined}
                placeholder={leftOutPlaceholder(v)}
                value={strVal}
                onChange={(e) => { setScalar(v.name, e.target.value); }}
                disabled={disabled}
                className="font-mono"
              />
            ) : (
              <Input
                id={id}
                type="text"
                placeholder={leftOutPlaceholder(v)}
                value={strVal}
                onChange={(e) => { setScalar(v.name, e.target.value); }}
                disabled={disabled}
              />
            )}

            {isWholeNumberVariable(v.varType) && v.min !== null && v.max !== null ? (
              <p className="text-xs text-muted-foreground">
                Value must be between {String(v.min)} and {String(v.max)}
              </p>
            ) : null}
          </div>
        );
      })}
    </>
  );
}
